import { useState } from 'react'
import { FolderPlus } from 'lucide-react'
import {
  Button,
  cn,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@valiant/ui-shared'
import { toast } from 'sonner'
import { useNavigation } from '../../../contexts/NavigationContext'
import { Chain } from '../../../generated/graphql'

export interface WhitelistedAddress {
  id: string
  address: string
  chain: string
  label?: string | null
}

interface FormErrors {
  title?: string
  address?: string
  chain?: string
}

interface AddWhitelistedAddressContentProps {
  className?: string
  onSuccess?: () => void
  existingAddresses: WhitelistedAddress[]
  onCreateWhitelistedAddress: (variables: {
    input: { companyId: string; chain: Chain; address: string; label: string }
  }) => Promise<{ data?: unknown }>
}

export function AddWhitelistedAddressContent({
  className,
  onSuccess,
  existingAddresses,
  onCreateWhitelistedAddress,
}: AddWhitelistedAddressContentProps) {
  const [title, setTitle] = useState('')
  const [address, setAddress] = useState('')
  const [chain, setChain] = useState<Chain | ''>('')
  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { selectedCompanyId, selectedCompany } = useNavigation()

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    // Title validation
    if (!title.trim()) {
      newErrors.title = 'Title is required'
    } else {
      // Check if title already exists
      const titleExists = existingAddresses.some(
        (addr) => addr.label?.toLowerCase() === title.trim().toLowerCase(),
      )
      if (titleExists) {
        newErrors.title = 'This name is already taken'
      }
    }

    // Address validation
    if (!address.trim()) {
      newErrors.address = 'Address is required'
    } else {
      // Check if address already exists
      const addressExists = existingAddresses.some(
        (addr) =>
          addr.chain === chain && addr.address.toLowerCase() === address.trim().toLowerCase(),
      )
      if (addressExists) {
        newErrors.address = 'This address is already whitelisted'
      } else {
        // Basic address validation based on chain
        const trimmedAddress = address.trim()
        if (chain === Chain.Btc) {
          // Bitcoin address validation (basic)
          if (!/^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$|^bc1[a-z0-9]{39,59}$/.test(trimmedAddress)) {
            newErrors.address = 'Please enter a valid BTC address'
          }
        } else if (chain === Chain.Bch) {
          // Bitcoin Cash address validation (legacy format or CashAddr format)
          if (
            !/^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$|^(bitcoincash:)?(q|p)[a-z0-9]{41}$/.test(
              trimmedAddress,
            )
          ) {
            newErrors.address = 'Please enter a valid BCH address'
          }
        } else if (chain === Chain.Ltc) {
          // Litecoin address validation (native SegWit ltc1... or legacy L.../M...)
          if (!/^ltc1[a-z0-9]{39,59}$|^[LM][a-km-zA-HJ-NP-Z1-9]{26,33}$/.test(trimmedAddress)) {
            newErrors.address = 'Please enter a valid LTC address'
          }
        } else if (chain === Chain.Eth) {
          // Ethereum address validation
          if (!/^0x[a-fA-F0-9]{40}$/.test(trimmedAddress)) {
            newErrors.address = 'Please enter a valid ETH address'
          }
        } else if (chain === Chain.Bsc) {
          // BSC address validation (EVM)
          if (!/^0x[a-fA-F0-9]{40}$/.test(trimmedAddress)) {
            newErrors.address = 'Please enter a valid BSC address'
          }
        } else if (chain === Chain.Polygon) {
          if (!/^0x[a-fA-F0-9]{40}$/.test(trimmedAddress)) {
            newErrors.address = 'Please enter a valid Polygon address'
          }
        } else if (chain === Chain.Solana) {
          if (!/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(trimmedAddress)) {
            newErrors.address = 'Please enter a valid Solana address'
          }
        } else if (chain === Chain.Tron) {
          // Tron address validation
          if (!/^T[A-Za-z1-9]{33}$/.test(trimmedAddress)) {
            newErrors.address = 'Please enter a valid TRON address'
          }
        } else if (trimmedAddress.length < 10) {
          newErrors.address = 'Please enter a valid address'
        }
      }
    }

    if (!chain) {
      newErrors.chain = 'Chain is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm() || !selectedCompanyId) {
      return
    }

    setIsSubmitting(true)
    try {
      const result = await onCreateWhitelistedAddress({
        input: {
          companyId: selectedCompanyId,
          chain: chain as Chain,
          address: address.trim(),
          label: title.trim(),
        },
      })

      if (result.data) {
        toast.success('Address whitelisting has been requested!')
        handleReset()
        onSuccess?.()
      } else {
        toast.error('Something went wrong. Please try again.')
      }
    } catch (error) {
      console.error('Error creating whitelisted address:', error)
      toast.error('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReset = () => {
    setTitle('')
    setAddress('')
    setChain('')
    setErrors({})
  }

  return (
    <div className={cn('flex flex-col h-full p-5', className)}>
      <div className="mb-8 md:mb-12.5">
        <div className="flex items-center gap-2">
          <img
            src="/icons/add-whitelisted-address.svg"
            alt="Add Whitelisted Address"
            className="w-5 h-5"
          />
          <h3 className="font-semibold text-2xl">Add Whitelisted Address</h3>
        </div>
        <p className="text-xl text-card-text mt-5 leading-none font-semibold">
          {selectedCompany?.company}
        </p>
      </div>

      <div className="flex flex-col justify-between h-full pb-4">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title" className="text-sm font-medium text-gray-700">
              Title
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value)
                if (errors.title) {
                  setErrors((prev) => ({ ...prev, title: undefined }))
                }
              }}
              placeholder="Enter a name for this address"
              className={errors.title ? 'border-red-500' : ''}
            />
            {errors.title && <p className="text-sm text-red-600">{errors.title}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="address" className="text-sm font-medium text-gray-700">
              Address
            </Label>
            <Input
              id="address"
              value={address}
              onChange={(e) => {
                setAddress(e.target.value)
                if (errors.address) {
                  setErrors((prev) => ({ ...prev, address: undefined }))
                }
              }}
              placeholder="Enter wallet address"
              className={errors.address ? 'border-red-500' : ''}
            />
            {errors.address && <p className="text-sm text-red-600">{errors.address}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="chain" className="text-sm font-medium text-gray-700">
              Chain
            </Label>
            <Select
              value={chain}
              onValueChange={(value) => {
                setChain(value as Chain)
                if (errors.chain) {
                  setErrors((prev) => ({ ...prev, chain: undefined }))
                }
              }}
            >
              <SelectTrigger className={cn('w-full', errors.chain ? 'border-red-500' : '')}>
                <SelectValue placeholder="Select chain" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={Chain.Btc}>BTC</SelectItem>
                <SelectItem value={Chain.Bch}>BCH</SelectItem>
                <SelectItem value={Chain.Ltc}>LTC</SelectItem>
                <SelectItem value={Chain.Eth}>ETH</SelectItem>
                <SelectItem value={Chain.Bsc}>BSC</SelectItem>
                <SelectItem value={Chain.Polygon}>Polygon</SelectItem>
                <SelectItem value={Chain.Solana}>Solana</SelectItem>
                <SelectItem value={Chain.Tron}>TRON</SelectItem>
              </SelectContent>
            </Select>
            {errors.chain && <p className="text-sm text-red-600">{errors.chain}</p>}
          </div>
        </div>

        <div className="mt-7">
          <Button
            className="w-full h-[40px]"
            size="lg"
            variant="main_action"
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedCompanyId}
          >
            <FolderPlus className="w-5 h-5 mr-2" />
            {isSubmitting ? 'Adding...' : 'Add'}
          </Button>
        </div>
      </div>
    </div>
  )
}
